import axios from "axios";
import * as aux from "../../auxilaries";
import {
    AdminGlobalSetupSections,
    AdminGlobalSetupSectionNames,
    ArchiveImpactOnRequests,
    PostedRequestDisplay,
} from '../../Enums';

export const noop = () => { };

export const rootURL = `${process.env.REACT_APP_API_DOTNET}/api/v1/admin/globalConfiguration`;

// To avoid circular dependency imports
export {
    AdminGlobalSetupSections,
    AdminGlobalSetupSectionNames,
    ArchiveImpactOnRequests,
    PostedRequestDisplay,
};

// TODO - Put this typing definitions in ~./typings

/**
 * @typedef {{
 *  archiving: { archiveInactiveDays: number; archiveImpactOnRequests: ApiCommon.ArchiveImpactOnRequests; };
 *  displayPreferences: {
 *      lastVisibleOrgPathLength: number;
 *      postedRequestDisplay: number;
 *      isShowOrganizationalPath: boolean;
 *  };
 *  employeeReports: { isAllEmployeeOffersEnabled: boolean; }
 * }} GlobalConfigs
 *
 *
 * @typedef {{
 * configs: GlobalConfigs;
 * unchangedConfigs: GlobalConfigs;
 * }} ILoadedConfigs
 *
 *
 * @typedef {(loadedConfigs: ILoadedConfigs) => unknown} IOnSuccess
 * @typedef {(message: string) => unknown} IOnError
 *
 * @typedef {GlobalConfigs & {
 *  configs: GlobalConfigs;
 *  unchangedConfigs: GlobalConfigs;
 *  showConfirmModal: boolean;
 *  confirmModelSection: string;
 *  typeSnackbar: string;
 *  messageSnackbar: string;
 *  openSnackbar: boolean;
 *  isMultipleAssignmentFeatureEnabled: boolean;
 * }} InitialStateGlobalConfigurations
 *
 */

export const defaultConfigs = {
    archiving: {
        archiveInactiveDays: 0,
        archiveImpactOnRequests: ArchiveImpactOnRequests.archiveAfterRequestCompletion,
    },
    displayPreferences: {
        lastVisibleOrgPathLength: 0,
        postedRequestDisplay: PostedRequestDisplay.requestName,
        isShowOrganizationalPath: false
    },
    employeeReports: {
        isAllEmployeeOffersEnabled: false,
    },
    shiftSwapSetup: {
        overrideMonthsInFuture: false,
        requestPeriodNumberOfMonthsInFuture: 1,
    },
    manualOverride: {
        manualOverride: false,
    },
    global: {
        isUseMultipleAssignment: false,
    }
};

/** @type {InitialStateGlobalConfigurations} */
export const initialStateGlobalConfigurations = {
    configs: defaultConfigs,
    unchangedConfigs: defaultConfigs,
    showConfirmModal: false,
    confirmModelSection: '',
    typeSnackbar: 'success',
    messageSnackbar: '',
    openSnackbar: false,
    isMultipleAssignmentFeatureEnabled: false,
};

export const getUpdatedStateWithUnchangedConfigs = (unchangedConfigs, updatedState) => ({
    unchangedConfigs: {
        ...unchangedConfigs,
        ...updatedState,
    },
    showConfirmModal: false,
    confirmModelSection: '',
});

/**
 * @returns {{
 *  route: string;
 *  data: *;
 *  globalConfigToUpdate: {
 *     unchangedConfigs: *;
 *     showConfirmModal: boolean;
 *     confirmModelSection: string;
 * };
 * }|undefined}
 */
export const getGlobalConfigParamsToUpdate = (
    unchangedConfigs,
    confirmModelSection,
    AdminGlobalSetupSections,
    AdminGlobalSetupSectionNames,
    configs,
) => {
    const params = {};

    if (confirmModelSection === AdminGlobalSetupSections.archiving) {
        params.route = AdminGlobalSetupSectionNames.archiving;
        params.data = { archiveDays: configs.archiving.archiveInactiveDays, archiveImpactOnRequests: configs.archiving.archiveImpactOnRequests }
        params.globalConfigToUpdate = getUpdatedStateWithUnchangedConfigs(unchangedConfigs, { archiving: { ...configs.archiving } });
    } else if (confirmModelSection === AdminGlobalSetupSections.displayPreferences) {
        params.route = AdminGlobalSetupSectionNames.displayPreferences;
        params.data = { ...configs.displayPreferences };
        params.globalConfigToUpdate = getUpdatedStateWithUnchangedConfigs(unchangedConfigs, { displayPreferences: { ...configs.displayPreferences } });
    } else if (confirmModelSection === AdminGlobalSetupSections.employeeReports) {
        params.route = AdminGlobalSetupSectionNames.employeeReports;
        params.data = { ...configs.employeeReports };
        params.globalConfigToUpdate = getUpdatedStateWithUnchangedConfigs(unchangedConfigs, { employeeReports: { ...configs.employeeReports } });
    } else if (confirmModelSection === AdminGlobalSetupSections.shiftSwapSetup) {
        params.route = AdminGlobalSetupSectionNames.shiftSwapSetup;
        params.data = { ...configs.shiftSwapSetup };
        params.globalConfigToUpdate = getUpdatedStateWithUnchangedConfigs(unchangedConfigs, { shiftSwapSetup: { ...configs.shiftSwapSetup } });
    } else if (confirmModelSection === AdminGlobalSetupSections.manualOverride) {
        params.route = AdminGlobalSetupSectionNames.manualOverride;
        params.data = { ...configs.manualOverride };
        params.globalConfigToUpdate = getUpdatedStateWithUnchangedConfigs(unchangedConfigs, { manualOverride: { ...configs.manualOverride } });
    } else if (confirmModelSection === AdminGlobalSetupSections.global) {
        params.route = AdminGlobalSetupSectionNames.global;
        params.data = { ...configs.global };
        params.globalConfigToUpdate = getUpdatedStateWithUnchangedConfigs(unchangedConfigs, { global: { ...configs.global } });
    }

    return Object.keys(params).length ? params : undefined;
};


export const getGlobalConfigurations = (
    options,
    /** @type {IOnSuccess} */
    onSuccess = noop,
    /** @type {IOnError} */
    onError = noop,
) => axios.get(rootURL, options)
    .then(({ data }) => {
        if (!data) {
            return;
        }

        const {
            archiveDays,
            archiveImpactOnRequests,
            isAllEmployeeOffersEnabled,
            isShowOrganizationalPath,
            lastVisibleOrgPathLength,
            postedRequestDisplay,
            requestPeriodNumberOfMonthsInFuture,
            manualOverride,
            isUseMultipleAssignment,
            isMultipleAssignmentFeatureEnabled,
        } = data;

        onSuccess({
            isMultipleAssignmentFeatureEnabled,
            configs: {
                archiving: {
                    archiveInactiveDays: archiveDays,
                    archiveImpactOnRequests: archiveImpactOnRequests,
                },
                displayPreferences: {
                    lastVisibleOrgPathLength: lastVisibleOrgPathLength,
                    postedRequestDisplay: postedRequestDisplay,
                    isShowOrganizationalPath: isShowOrganizationalPath
                },
                employeeReports: {
                    isAllEmployeeOffersEnabled,
                },
                shiftSwapSetup: {
                    overrideMonthsInFuture: requestPeriodNumberOfMonthsInFuture !== defaultConfigs.shiftSwapSetup.requestPeriodNumberOfMonthsInFuture,
                    requestPeriodNumberOfMonthsInFuture
                },
                manualOverride: {
                    manualOverride: manualOverride ?? false,
                },
                global: {
                    isUseMultipleAssignment: isUseMultipleAssignment ?? false,
                }
            },
            unchangedConfigs: {
                archiving: {
                    archiveInactiveDays: archiveDays,
                    archiveImpactOnRequests: archiveImpactOnRequests,
                },
                displayPreferences: {
                    lastVisibleOrgPathLength: lastVisibleOrgPathLength,
                    postedRequestDisplay: postedRequestDisplay,
                    isShowOrganizationalPath: isShowOrganizationalPath
                },
                employeeReports: {
                    isAllEmployeeOffersEnabled,
                },
                shiftSwapSetup: {
                    overrideMonthsInFuture: requestPeriodNumberOfMonthsInFuture !== defaultConfigs.shiftSwapSetup.requestPeriodNumberOfMonthsInFuture,
                    requestPeriodNumberOfMonthsInFuture
                },
                manualOverride: {
                    manualOverride: manualOverride ?? false,
                },
                global: {
                    isUseMultipleAssignment: isUseMultipleAssignment ?? false,
                }
            }
        });
    })
    .catch((err) => {
        const message = aux.handleAPIErrors(err.response);
        onError(message);
    });

export const updateGlobalConfiguration = (
    endpoint,
    data,
    options,
    onSuccess = noop,
    /** @type {IOnError} */
    onError = noop,
) => axios.put(`${rootURL}/${endpoint}`, data, options)
    .then(onSuccess)
    .catch((err) => {
        const message = aux.handleAPIErrors(err.response);
        onError(message);
    });
